<template>
  <v-card :loading="isLoading" flat class="payment-monitoring-page">
    <v-card-title class="payment-monitoring-page-header align-start flex-wrap">
      <div>
        <h1 class="payment-monitoring-page-title">Otomatik Talimatlar</h1>
        <div class="payment-monitoring-page-subtitle">
          TYA'nın aktif POS sağlayıcısına ait talimatları ve ödeme durumlarını
          inceleyin.
        </div>
      </div>
      <v-spacer />
      <v-btn small outlined color="primary" @click="loadSubscriptions">
        <v-icon small left>mdi-refresh</v-icon>
        Yenile
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-0">
      <div class="payment-monitoring-context-row">
        <span class="payment-monitoring-context-chip">
          Görünüm: <strong>Güncel talimat envanteri</strong>
        </span>
        <span class="payment-monitoring-context-chip">
          Son durum: <strong>{{ lastScanLabel }}</strong>
        </span>
        <span class="payment-monitoring-context-chip">
          Otomatik tarama: <strong>{{ scheduleLabel }}</strong>
        </span>
      </div>

      <v-sheet outlined class="subscription-filters">
        <v-row dense align="center">
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="filters.paymentProviderIds"
              :items="paymentProviderList"
              item-text="name"
              item-value="id"
              label="Sağlayıcı"
              prepend-inner-icon="mdi-source-branch"
              multiple
              clearable
              dense
              outlined
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <select-cluster
              v-model="filters.clusterId"
              admin
              clearable
              dense
              outlined
              label="TYA"
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="filters.hasOpenDebt"
              :items="openDebtItems"
              item-text="text"
              item-value="id"
              label="Açık borç"
              prepend-inner-icon="mdi-cash-clock"
              clearable
              dense
              outlined
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model.trim="filters.query"
              label="Talimat no, kişi adı, TYA veya telefon"
              prepend-inner-icon="mdi-magnify"
              clearable
              dense
              outlined
              hide-details="auto"
              @keyup.enter="applyFilters"
            />
          </v-col>
          <v-col cols="12" sm="6" md="2" class="d-flex justify-end">
            <v-btn
              color="primary"
              block
              class="subscription-filter-button"
              @click="applyFilters"
            >
              <v-icon small left>mdi-filter</v-icon>
              Filtrele
            </v-btn>
          </v-col>
        </v-row>
      </v-sheet>

      <v-row dense class="subscription-metrics-row">
        <v-col
          v-for="metric in metrics"
          :key="metric.label"
          cols="12"
          sm="6"
          md="3"
        >
          <payment-monitoring-metric v-bind="metric" />
        </v-col>
      </v-row>

      <v-card outlined class="payment-monitoring-panel mb-5">
        <div
          class="payment-monitoring-panel-header d-flex flex-wrap justify-space-between align-center"
        >
          <div>
            <div class="payment-monitoring-section-title">Talimat durumu</div>
            <div class="payment-monitoring-section-description">
              Aktif, takip gerektiren ve iptal edilmiş talimatları filtreleyin.
            </div>
          </div>
          <v-chip small outlined>Aktif filtre: {{ activeSegmentLabel }}</v-chip>
        </div>
        <v-btn-toggle
          v-model="activeSegment"
          mandatory
          color="primary"
          class="subscription-segment-grid"
          @change="applySegment"
        >
          <v-btn
            v-for="segment in segments"
            :key="segment.id"
            :value="segment.id"
            :class="`subscription-segment subscription-segment--${segment.id}`"
          >
            <v-icon left>{{ segment.icon }}</v-icon>
            <span>{{ segment.text }}</span>
            <b>{{ segmentValue(segment.id) }}</b>
          </v-btn>
        </v-btn-toggle>
      </v-card>

      <v-card outlined class="payment-monitoring-panel">
        <v-card-title class="payment-monitoring-panel-header">
          <div>
            <div class="payment-monitoring-section-title">
              Talimat envanteri
            </div>
            <div class="payment-monitoring-section-description">
              Her satır talimat, kart, sağlayıcı, açık borç ve son ödeme
              durumunu özetler.
            </div>
          </div>
        </v-card-title>
        <v-data-table
          :headers="headers"
          :items="response.items || []"
          :loading="isLoading"
          :server-items-length="response.pagination.total || 0"
          :options.sync="options"
          :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
        >
          <template v-slot:item.created_at="{ item }">
            <strong class="subscription-id">#{{ item.subscription_id }}</strong>
            <div class="caption text--secondary">
              {{ formatDate(item.created_at) }}
            </div>
          </template>
          <template v-slot:item.cluster_name="{ item }">
            <strong>{{ item.cluster_name }}</strong>
            <div class="caption text--secondary">
              {{ item.block_name || "-" }}-{{ item.door_number || "-" }}
            </div>
          </template>
          <template v-slot:item.card_provider_name="{ item }">
            <span class="subscription-provider-badge">{{
              item.active_provider_name || item.card_provider_name
            }}</span>
          </template>
          <template v-slot:item.resident_name="{ item }">
            <strong>{{ item.resident_name || "-" }}</strong>
            <div class="caption text--secondary">
              {{ item.resident_phone || "-" }}
            </div>
          </template>
          <template v-slot:item.card_status="{ item }">
            <div class="subscription-card-cell">
              <v-icon small :color="cardStateMeta(item).color">
                mdi-credit-card-outline
              </v-icon>
              <strong>•••• {{ item.card_mask_suffix || "--" }}</strong>
            </div>
            <div class="caption" :class="`${cardStateMeta(item).color}--text`">
              {{ cardStateMeta(item).text }}
            </div>
          </template>
          <template v-slot:item.open_debt_amount="{ item }">
            <rs-table-cell-number price :value="item.open_debt_amount" />
            <div class="caption text--secondary">
              {{ item.open_debt_count }} açık borç
            </div>
          </template>
          <template v-slot:item.latest_payment_at="{ item }">
            <template v-if="item.latest_payment_id">
              <strong>#{{ item.latest_payment_id }}</strong>
              <span class="ml-1">{{
                paymentStatusMeta(item.latest_payment_status).text
              }}</span>
              <div class="caption text--secondary">
                {{ formatDate(item.latest_payment_at) }}
              </div>
            </template>
            <span v-else class="text--secondary">Henüz ödeme yok</span>
          </template>
          <template v-slot:item.collection_status="{ item }">
            <v-chip x-small dark :color="lifecycleMeta(item).color">
              {{ lifecycleMeta(item).text }}
            </v-chip>
            <div
              v-if="item.lifecycle_status === 'active'"
              class="subscription-health-reason"
            >
              {{ operationMeta(item).text }}
            </div>
            <div v-else class="subscription-health-reason">
              {{ formatDate(item.cancelled_at) }}
            </div>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="openDetail(item)">
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </v-card-text>

    <v-navigation-drawer
      v-model="detailOpen"
      temporary
      fixed
      right
      width="620"
      class="payment-monitoring-drawer"
    >
      <div v-if="isDetailLoading" class="subscription-detail-loading">
        <v-progress-circular indeterminate color="primary" />
        <span>Talimat detayı yükleniyor...</span>
      </div>
      <v-card v-else-if="detailItem" flat class="subscription-detail-card">
        <v-card-title class="subscription-detail-header">
          <div>
            <div class="subscription-detail-title">
              Talimat #{{ detailItem.subscription_id }}
            </div>
            <div class="caption text--secondary">
              {{ detailItem.resident_name }} · {{ detailItem.cluster_name }}
            </div>
          </div>
          <v-spacer />
          <v-chip x-small dark :color="lifecycleMeta(detailItem).color">
            {{ lifecycleMeta(detailItem).text }}
          </v-chip>
          <v-btn icon aria-label="Detayı kapat" @click="detailOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="subscription-detail-body">
          <v-alert
            v-if="detailItem.operation_status === 'attention'"
            dense
            outlined
            type="warning"
            class="subscription-detail-alert"
          >
            {{ reasonText(detailItem.health_reason) }}
          </v-alert>

          <section class="subscription-detail-section">
            <div class="subscription-detail-heading">
              <div class="subscription-detail-section-title">
                Bekleyen tahsilat
              </div>
              <span class="subscription-panel-summary">
                {{ pendingDebts(detailItem).length }} borç
              </span>
            </div>
            <div
              v-if="pendingDebts(detailItem).length"
              class="subscription-pending-debts"
            >
              <div
                v-for="debt in pendingDebts(detailItem)"
                :key="debt.debt_id"
                class="subscription-pending-debt"
              >
                <div class="subscription-pending-debt-main">
                  <strong>{{ debt.description || "Borç kaydı" }}</strong>
                  <small>Borç #{{ debt.debt_id }}</small>
                </div>
                <div>
                  <span>Vade</span>
                  <strong>{{ dateText(debt.due_on) }}</strong>
                </div>
                <div>
                  <span>Kalan tutar</span>
                  <strong>{{ moneyText(debt.remaining_amount) }}</strong>
                </div>
                <div class="subscription-pending-debt-plan">
                  <span>
                    <v-icon x-small>mdi-calendar-clock</v-icon>
                    Planlanan tarih
                  </span>
                  <strong v-if="debt.planned_at">
                    {{ formatDate(debt.planned_at) }}
                  </strong>
                  <v-chip
                    v-else-if="debt.retry_status"
                    x-small
                    dark
                    :color="debt.retry_status === 'queued' ? 'info' : 'warning'"
                    class="subscription-pending-debt-retry"
                  >
                    <v-icon
                      x-small
                      left
                      :class="{
                        'mdi-spin': debt.retry_status === 'queueing',
                      }"
                    >
                      {{
                        debt.retry_status === "queued"
                          ? "mdi-clock-check-outline"
                          : "mdi-loading"
                      }}
                    </v-icon>
                    {{
                      debt.retry_status === "queued"
                        ? "Sıraya alındı"
                        : "Sıraya alınıyor"
                    }}
                  </v-chip>
                  <v-btn
                    v-else-if="canRetryDebt(detailItem)"
                    x-small
                    depressed
                    dark
                    color="warning"
                    class="subscription-pending-debt-retry"
                    title="Bu borç için tahsilatı yeniden dene"
                    @click="retryDebt(debt)"
                  >
                    <v-icon x-small left>mdi-pause-circle</v-icon>
                    Durduruldu
                  </v-btn>
                  <strong v-else class="warning--text">
                    <v-icon x-small color="warning">mdi-pause-circle</v-icon>
                    Durduruldu
                  </strong>
                </div>
              </div>
            </div>
            <div v-else class="subscription-detail-empty">
              Bu talimat için planlanmış açık borç yok.
            </div>
          </section>

          <v-expansion-panels flat class="subscription-detail-panels">
            <v-expansion-panel>
              <v-expansion-panel-header>
                <span>Kart ve sağlayıcı</span>
                <v-chip
                  x-small
                  dark
                  :color="cardProviderMeta(detailItem).color"
                  class="subscription-status-chip mr-2"
                >
                  {{ cardProviderMeta(detailItem).text }}
                </v-chip>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="subscription-detail-list">
                  <div class="subscription-detail-row">
                    <span>Aktif sağlayıcı</span>
                    <strong>{{
                      detailItem.active_provider_name || "-"
                    }}</strong>
                  </div>
                  <div class="subscription-detail-row">
                    <span>Kayıtlı kart</span>
                    <strong
                      >{{ detailItem.card_provider_name }} · ••••
                      {{ detailItem.card_mask_suffix || "--" }}</strong
                    >
                  </div>
                  <div class="subscription-detail-row">
                    <span>Son kullanma</span>
                    <strong>{{ detailItem.card_expires_on || "-" }}</strong>
                  </div>
                  <div class="subscription-detail-row">
                    <span>Kart durumu</span>
                    <strong>{{ cardStateMeta(detailItem).text }}</strong>
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <section class="subscription-detail-section">
            <div class="subscription-detail-heading">
              <div class="subscription-detail-section-title">Son 10 deneme</div>
              <router-link
                :to="transactionRoute(detailItem)"
                target="_blank"
                rel="noopener noreferrer"
              >
                <v-icon x-small color="primary" class="mr-1"
                  >mdi-open-in-new</v-icon
                >
                Tüm ödemeler
              </router-link>
            </div>
            <div
              v-if="!(detailItem.debt_attempt_groups || []).length"
              class="subscription-detail-empty"
            >
              Bu talimata bağlı deneme bulunamadı.
            </div>
            <v-expansion-panels
              v-else
              accordion
              flat
              class="subscription-debt-attempt-panels"
            >
              <v-expansion-panel
                v-for="group in detailItem.debt_attempt_groups || []"
                :key="group.debt_id || 'unmatched'"
              >
                <v-expansion-panel-header>
                  <div class="subscription-debt-attempt-heading">
                    <div>
                      <strong>{{ group.description }}</strong>
                      <span v-if="group.debt_id"
                        >Borç #{{ group.debt_id }}</span
                      >
                      <span v-else>Borç eşleşmesi yok</span>
                    </div>
                    <div class="text-right">
                      <strong>{{ attemptGroupText(group) }}</strong>
                      <v-chip
                        x-small
                        dark
                        class="subscription-status-chip"
                        :color="
                          paymentStatusMeta(group.attempts[0].status).color
                        "
                      >
                        {{ paymentStatusMeta(group.attempts[0].status).text }}
                      </v-chip>
                    </div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <article
                    v-for="attempt in group.attempts"
                    :key="attempt.attempt_id"
                    class="subscription-history-row"
                  >
                    <div>
                      <strong>{{ formatDate(attempt.attempted_at) }}</strong>
                      <span>
                        Ödeme #{{ attempt.payment_id }} · Deneme #{{
                          attempt.attempt_id
                        }}
                      </span>
                    </div>
                    <div class="subscription-history-amount">
                      {{ moneyText(attempt.amount) }}
                    </div>
                    <div class="text-right">
                      <strong
                        :class="`${
                          paymentStatusMeta(attempt.status).color
                        }--text`"
                      >
                        {{ paymentStatusMeta(attempt.status).text }}
                      </strong>
                      <span>{{
                        attempt.status_reason || "Sağlayıcı sonucu yok"
                      }}</span>
                    </div>
                  </article>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </section>

          <section class="subscription-detail-section">
            <div class="subscription-detail-section-title">İlgili kayıtlar</div>
            <div class="subscription-detail-links">
              <router-link
                v-if="can('see-house-income')"
                :to="{
                  name: 'incomes.residents.show',
                  params: { id: detailItem.resident_id },
                }"
                target="_blank"
                rel="noopener noreferrer"
              >
                <v-icon x-small color="primary" class="mr-1"
                  >mdi-open-in-new</v-icon
                >
                Mali işlemler
              </router-link>
              <router-link
                v-if="can('see-house-income')"
                :to="{
                  name: 'incomes.houses.show',
                  params: { id: detailItem.house_id },
                }"
                target="_blank"
                rel="noopener noreferrer"
              >
                <v-icon x-small color="primary" class="mr-1"
                  >mdi-open-in-new</v-icon
                >
                Bağımsız bölüm
              </router-link>
            </div>
          </section>
        </v-card-text>
      </v-card>
    </v-navigation-drawer>
  </v-card>
</template>

<script>
import { hasPermissions, isPage } from "@/view/mixins";
import { mapGetters } from "vuex";
import SelectCluster from "@/view/content/forms/SelectCluster.vue";
import PaymentMonitoringMetric from "./components/PaymentMonitoringMetric.vue";
import {
  formatMonitoringDate,
  paymentMonitoringOptionsFromQuery,
} from "./paymentMonitoring";

const listFromQuery = (value) =>
  (Array.isArray(value) ? value : [value])
    .reduce((items, item) => items.concat(String(item || "").split(",")), [])
    .filter(Boolean);
const integerListFromQuery = (value) =>
  listFromQuery(value)
    .map(Number)
    .filter((value) => Number.isInteger(value) && value > 0);
const queryValue = (value) =>
  Array.isArray(value) ? value[value.length - 1] : value;
const emptyResponse = () => ({
  meta: {},
  summary: {},
  items: [],
  pagination: { total: 0 },
});
const segmentIds = ["all", "active", "attention", "cancelled"];

export default {
  name: "AutoChargeSubscriptions",
  components: { PaymentMonitoringMetric, SelectCluster },
  mixins: [hasPermissions, isPage],
  data() {
    const routeSegment = queryValue(this.$route.query.segment);
    const openDebt = queryValue(this.$route.query.has_open_debt);
    return {
      pageMeta: { title: "Otomatik Talimatlar" },
      isLoading: false,
      isDetailLoading: false,
      detailOpen: false,
      detailItem: null,
      response: emptyResponse(),
      activeSegment: segmentIds.includes(routeSegment) ? routeSegment : "all",
      filters: {
        paymentProviderIds: integerListFromQuery(
          this.$route.query["payment_provider_ids[]"]
        ),
        clusterId:
          integerListFromQuery(this.$route.query["cluster_ids[]"])[0] || null,
        hasOpenDebt:
          openDebt === "true" ? true : openDebt === "false" ? false : null,
        query: queryValue(this.$route.query.query) || null,
      },
      options: paymentMonitoringOptionsFromQuery(this.$route.query, {
        page: 1,
        itemsPerPage: 20,
        sortBy: ["created_at"],
        sortDesc: [true],
      }),
      openDebtItems: [
        { id: true, text: "Açık borcu var" },
        { id: false, text: "Açık borcu yok" },
      ],
      segments: [
        {
          id: "all",
          text: "Tümü",
          icon: "mdi-view-grid-outline",
          statuses: [],
        },
        {
          id: "active",
          text: "Aktif",
          icon: "mdi-check-circle-outline",
          statuses: ["ready", "waiting", "success", "failed", "attention"],
        },
        {
          id: "attention",
          text: "Takip gerektiren",
          icon: "mdi-alert-circle-outline",
          statuses: ["attention"],
        },
        {
          id: "cancelled",
          text: "İptal",
          icon: "mdi-cancel",
          statuses: ["cancelled"],
        },
      ],
      headers: [
        { text: "Talimat", value: "created_at", width: "120px" },
        { text: "TYA / BB", value: "cluster_name" },
        { text: "Sağlayıcı", value: "card_provider_name", width: "105px" },
        { text: "Sakin", value: "resident_name" },
        {
          text: "Kart",
          value: "card_status",
          sortable: false,
          width: "120px",
        },
        { text: "Açık borç", value: "open_debt_amount", align: "end" },
        { text: "Son ödeme", value: "latest_payment_at", width: "150px" },
        {
          text: "Durum",
          value: "collection_status",
          sortable: false,
          width: "165px",
        },
        { text: "", value: "actions", sortable: false, width: "40px" },
      ],
    };
  },
  computed: {
    ...mapGetters(["paymentProviderList"]),
    metrics() {
      const value = this.response.summary || {};
      return [
        {
          label: "Tüm talimatlar",
          value: value.item_count || 0,
          caption: "Güncel talimat envanteri",
          icon: "mdi-credit-card-clock-outline",
          color: "primary",
          footLabel: "Aktif",
          footValue: value.active_count || 0,
        },
        {
          label: "Aktif",
          value: value.active_count || 0,
          caption: "Aktif sağlayıcı kapsamındaki talimatlar",
          icon: "mdi-shield-check-outline",
          color: "success",
          footLabel: "Sorunsuz",
          footValue: Math.max(
            (value.active_count || 0) - (value.attention_count || 0),
            0
          ),
        },
        {
          label: "Takip gerektiren",
          value: value.attention_count || 0,
          caption: "Aktif ancak müdahale bekleyen",
          icon: "mdi-alert-outline",
          color: "warning",
          footLabel: "Açık borç",
          footValue: value.open_debt_count || 0,
        },
        {
          label: "İptal",
          value: value.cancelled_count || 0,
          caption: "Aktif sağlayıcının geçmiş talimatları",
          icon: "mdi-cancel",
          color: "info",
        },
      ];
    },
    activeSegmentLabel() {
      return (
        this.segments.find((item) => item.id === this.activeSegment)?.text ||
        "Tümü"
      );
    },
    lastScanLabel() {
      const scan = this.response.meta?.last_scan;
      return scan?.finished_at
        ? `${formatMonitoringDate(scan.finished_at)} · ${this.scanStatusText(
            scan.status
          )}`
        : "Henüz kaydedilmedi";
    },
    scheduleLabel() {
      const slots = this.response.meta?.schedule_slots || [];
      return slots.length ? slots.join(" · ") : "09:00 · 12:00 · 15:00 · 18:00";
    },
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.syncUrlState();
        this.loadSubscriptions();
      },
    },
  },
  mounted() {
    this.syncUrlState();
    this.loadSubscriptions();
  },
  methods: {
    applyFilters() {
      if (this.options.page !== 1) this.options.page = 1;
      else {
        this.syncUrlState();
        this.loadSubscriptions();
      }
    },
    applySegment() {
      this.applyFilters();
    },
    requestParams() {
      const segment = this.segments.find(
        (item) => item.id === this.activeSegment
      );
      const params = {
        page: this.options.page,
        per_page: this.options.itemsPerPage,
        order_by: this.options.sortBy[0] || "created_at",
        order: this.options.sortDesc[0] ? "desc" : "asc",
        active_only: false,
      };
      if (this.filters.paymentProviderIds?.length)
        params.payment_provider_ids = this.filters.paymentProviderIds;
      if (this.filters.clusterId) params.cluster_ids = [this.filters.clusterId];
      if (segment?.statuses.length) params.statuses = segment.statuses;
      if (this.filters.hasOpenDebt !== null)
        params.has_open_debt = this.filters.hasOpenDebt;
      if (this.filters.query) params.query = this.filters.query;
      return params;
    },
    syncUrlState() {
      const params = new URLSearchParams();
      const request = this.requestParams();
      if (this.filters.paymentProviderIds?.length)
        params.set(
          "payment_provider_ids[]",
          this.filters.paymentProviderIds.join(",")
        );
      if (this.filters.clusterId)
        params.set("cluster_ids[]", String(this.filters.clusterId));
      if (this.filters.hasOpenDebt !== null)
        params.set("has_open_debt", String(this.filters.hasOpenDebt));
      if (this.filters.query) params.set("query", this.filters.query);
      params.set("segment", this.activeSegment);
      params.set("page", String(request.page));
      params.set("per_page", String(request.per_page));
      params.set("order_by", request.order_by);
      params.set("order", request.order);
      const search = params.toString();
      const url = `${window.location.pathname}?${search}${window.location.hash}`;
      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (url !== current)
        window.history.replaceState(window.history.state, "", url);
    },
    loadSubscriptions() {
      if (this.isLoading) return;
      this.isLoading = true;
      this.$api
        .query("api-super-admin/payment-monitoring/subscriptions", {
          params: this.requestParams(),
        })
        .then(
          (response) => (this.response = response.data.data || emptyResponse())
        )
        .catch((error) => this.handleError(error))
        .finally(() => (this.isLoading = false));
    },
    openDetail(item) {
      this.detailOpen = true;
      this.detailItem = null;
      this.isDetailLoading = true;
      this.$api
        .query(
          `api-super-admin/payment-monitoring/subscriptions/${item.subscription_id}`
        )
        .then(
          (response) => (this.detailItem = response.data.data?.item || null)
        )
        .catch((error) => {
          this.detailOpen = false;
          this.handleError(error);
        })
        .finally(() => (this.isDetailLoading = false));
    },
    canRetryDebt(item) {
      return item?.health_reason === "failed_attempt_limit_reached";
    },
    retryDebt(debt) {
      if (debt.retry_status) return;

      if (
        !window.confirm(
          `${debt.description || `Borç #${debt.debt_id}`} için ${this.moneyText(
            debt.remaining_amount
          )} tutarındaki tahsilat yeniden denensin mi?`
        )
      )
        return;

      this.$set(debt, "retry_status", "queueing");
      this.$api
        .post(
          `api-super-admin/payment-ops/subscriptions/${this.detailItem.subscription_id}/debts/${debt.debt_id}/retry`
        )
        .then((response) => {
          this.$set(
            debt,
            "retry_status",
            response.data.data?.status || "queued"
          );
          this.$toast.success("Tahsilat yeniden denenmek üzere sıraya alındı.");
        })
        .catch((error) => {
          this.$set(debt, "retry_status", null);
          this.handleError(error);
        });
    },
    segmentValue(id) {
      const value = this.response.summary || {};
      return {
        all: value.item_count || 0,
        active: value.active_count || 0,
        attention: value.attention_count || 0,
        cancelled: value.cancelled_count || 0,
      }[id];
    },
    lifecycleMeta(item) {
      return item.lifecycle_status === "cancelled" || item.cancelled_at
        ? { text: "İptal", color: "grey" }
        : { text: "Aktif", color: "success" };
    },
    operationMeta(item) {
      return (
        {
          attention: { text: "Takip gerektiren" },
          failed: { text: "Son ödeme başarısız" },
          waiting: { text: "Ödeme bekliyor" },
          success: { text: "Son ödeme başarılı" },
          ready: { text: "Hazır" },
        }[item.operation_status] || { text: "Durum bekleniyor" }
      );
    },
    cardStateMeta(item) {
      if (!item.card_is_active) return { text: "Pasif", color: "error" };
      if (item.card_status && item.card_status !== "valid")
        return { text: "Kullanılamaz", color: "error" };
      if (!item.card_has_provider_token)
        return { text: "Sağlayıcı kaydı eksik", color: "warning" };

      const now = new Date();
      const expiryThreshold = `${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, "0")}-15`;
      if (
        !item.card_expires_on ||
        String(item.card_expires_on).slice(0, 10) < expiryThreshold
      )
        return { text: "Süresi dolmuş", color: "error" };

      return { text: "Kullanılabilir", color: "success" };
    },
    cardProviderMeta(item) {
      const cardState = this.cardStateMeta(item);
      const providerIssues = [
        "active_cluster_provider_missing",
        "cluster_provider_subscribe_disabled",
        "cluster_provider_not_enabled",
        "user_card_provider_mismatch",
      ];

      return cardState.color !== "success" ||
        providerIssues.includes(item.health_reason)
        ? { text: "İnceleme gerekli", color: "warning" }
        : { text: "Uygun", color: "success" };
    },
    paymentStatusMeta(status) {
      return (
        {
          success: { text: "Başarılı", color: "success" },
          failed: { text: "Başarısız", color: "error" },
          pending: { text: "Bekliyor", color: "warning" },
          unknown: { text: "Sonuç belirsiz", color: "warning" },
          manual_review: { text: "İnceleme gerekli", color: "warning" },
          cancelled: { text: "İptal", color: "grey" },
          refunded: { text: "İade", color: "info" },
          created: { text: "Oluşturuldu", color: "info" },
        }[status] || { text: status || "-", color: "grey" }
      );
    },
    attemptGroupText(group) {
      return `${Number(group.attempt_count) || 0} deneme`;
    },
    reasonText(reason) {
      return (
        {
          ready: "Talimat ödeme almaya hazır.",
          eligible_debt_waiting:
            "Ödemeye uygun borç bir sonraki taramayı bekliyor.",
          latest_payment_success: "Son ödeme başarıyla tamamlandı.",
          latest_payment_failed:
            "Son ödeme başarısız; yeniden deneme hakkı var.",
          unresolved_payment: "Son ödeme sonucu kesinleşmedi.",
          failed_attempt_limit_reached:
            "Talimat üç ardışık başarısızlık nedeniyle durduruldu.",
          non_3ds_limit_exceeded:
            "Karta çekilecek tutar sağlayıcının non-3DS işlem limitini aşıyor.",
          subscription_cancelled: "Talimat iptal edilmiş.",
          payment_globally_disabled:
            "Kredi kartı ödemeleri genel olarak kapalı.",
          active_cluster_provider_missing:
            "TYA için aktif ödeme sağlayıcısı yok.",
          cluster_provider_subscribe_disabled:
            "Aktif sağlayıcıda otomatik talimat kapalı.",
          cluster_provider_not_enabled:
            "Aktif sağlayıcı çalışma ortamında etkin değil.",
          user_card_provider_mismatch:
            "Kayıtlı kart ile TYA'nın aktif sağlayıcısı farklı.",
          user_card_inactive: "Kayıtlı kart pasif.",
          user_card_status_not_valid:
            "Kayıtlı kart kullanılabilir durumda değil.",
          user_card_provider_token_missing:
            "Kartın sağlayıcı kayıt bilgisi eksik.",
          user_card_expired: "Kartın son kullanma tarihi geçmiş.",
        }[reason] ||
        reason ||
        "Durum bilgisi yok."
      );
    },
    transactionRoute(item) {
      const now = new Date();
      const today = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0"),
      ].join("-");

      return {
        name: "admin.payment-ops.transactions",
        query: {
          subscription_id: item.subscription_id,
          date_preset: "custom",
          "date_range[min]": String(item.created_at).slice(0, 10),
          "date_range[max]": today,
        },
      };
    },
    scanStatusText(status) {
      return (
        { completed: "Tamamlandı", failed: "Başarısız", skipped: "Atlandı" }[
          status
        ] ||
        status ||
        "-"
      );
    },
    moneyText(value) {
      return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
      }).format(Number(value || 0));
    },
    pendingDebts(item) {
      return item?.pending_debts || [];
    },
    dateText(value) {
      if (!value) return "-";

      return new Intl.DateTimeFormat("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(new Date(`${value}T00:00:00`));
    },
    formatDate: formatMonitoringDate,
  },
};
</script>

<style scoped>
@import "./payment-monitoring.scss";

.subscription-filters {
  margin-bottom: 18px;
  padding: 12px 14px;
  border-color: #e4e6ef !important;
  border-radius: 6px !important;
  background: #ffffff;
  box-shadow: 0 3px 12px rgba(29, 33, 68, 0.035);
}

.subscription-filters ::v-deep .v-input__slot {
  min-height: 40px !important;
  border-radius: 4px !important;
  background: #ffffff !important;
}

.subscription-filters ::v-deep .v-input__slot fieldset {
  border-color: #d8dbe6;
}

.subscription-filters ::v-deep .v-label,
.subscription-filters ::v-deep input,
.subscription-filters ::v-deep .v-select__selection {
  font-size: 12px;
  font-weight: 600;
}

.subscription-filter-button {
  min-height: 40px;
  border-radius: 4px;
  box-shadow: none;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.subscription-metrics-row {
  margin-bottom: 18px;
}

.subscription-segment-grid {
  display: grid;
  width: 100%;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border-radius: 0;
}

.subscription-segment-grid ::v-deep .v-btn {
  min-width: 0 !important;
  height: 58px !important;
  border: 0 !important;
  border-left: 3px solid #5867dd !important;
  border-radius: 0 !important;
  background: #fff !important;
  color: #3f4254 !important;
  text-transform: none;
}

.subscription-segment-grid ::v-deep .v-btn--active {
  background: #f8f9ff !important;
}

.subscription-segment-grid ::v-deep .v-btn__content {
  width: 100%;
  justify-content: flex-start;
}

.subscription-segment-grid b {
  margin-left: auto;
  color: #181c32;
  font-size: 18px;
}

.subscription-segment--active {
  border-left-color: #1bc5bd !important;
}
.subscription-segment--attention {
  border-left-color: #ffa800 !important;
}
.subscription-segment--cancelled {
  border-left-color: #7e8299 !important;
}

.subscription-id {
  color: #181c32;
}

.subscription-provider-badge {
  display: inline-flex;
  padding: 3px 8px;
  border-radius: 4px;
  background: #f0f2ff;
  color: #5867dd;
  font-weight: 700;
}

.subscription-card-cell {
  display: flex;
  align-items: center;
  gap: 5px;
}

.subscription-health-reason {
  max-width: 170px;
  margin-top: 4px;
  color: #7e8299;
  font-size: 10px;
  line-height: 1.35;
}

.subscription-detail-card {
  min-height: 100%;
}

.subscription-detail-loading {
  display: flex;
  min-height: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  color: #7e8299;
  font-size: 12px;
  font-weight: 600;
}

.subscription-detail-header {
  position: sticky;
  z-index: 2;
  top: 0;
  min-height: 74px;
  gap: 10px;
  background: #fff;
}

.subscription-detail-title {
  color: #181c32;
  font-size: 18px;
  font-weight: 700;
}

.subscription-detail-body {
  min-height: calc(100vh - 75px);
  padding: 20px 18px 28px !important;
  background: #f7f8fb;
}

.subscription-detail-alert {
  margin-bottom: 16px;
  border-radius: 8px;
}

.subscription-detail-panels {
  margin-bottom: 16px;
  overflow: hidden;
  border: 1px solid #e7e9f1;
  border-radius: 8px;
  background: #fff;
}

.subscription-detail-panels ::v-deep .v-expansion-panel + .v-expansion-panel {
  border-top: 1px solid #eff1f5;
}

.subscription-detail-panels ::v-deep .v-expansion-panel-header,
.subscription-debt-attempt-panels ::v-deep .v-expansion-panel-header {
  min-height: 52px;
  color: #181c32;
  font-size: 13px;
  font-weight: 700;
}

.subscription-detail-panels ::v-deep .v-expansion-panel-header {
  padding: 0 16px;
}

.subscription-detail-panels ::v-deep .v-expansion-panel-content__wrap,
.subscription-debt-attempt-panels ::v-deep .v-expansion-panel-content__wrap {
  padding: 0 16px 16px;
}

.subscription-panel-summary {
  margin-left: auto;
  color: #7e8299;
  font-size: 11px;
  font-weight: 600;
}

.subscription-detail-section {
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #e7e9f1;
  border-radius: 8px;
  background: #fff;
}

.subscription-detail-heading,
.subscription-detail-links {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.subscription-detail-heading {
  margin-bottom: 12px;
}

.subscription-detail-section-title {
  margin-bottom: 10px;
  color: #181c32;
  font-size: 13px;
  font-weight: 700;
}

.subscription-detail-heading .subscription-detail-section-title {
  margin-bottom: 0;
}

.subscription-debt-attempt-panels {
  margin-top: 0;
}

.subscription-debt-attempt-panels ::v-deep .v-expansion-panel {
  border-top: 1px solid #eff1f5;
}

.subscription-debt-attempt-panels ::v-deep .v-expansion-panel:last-child {
  border-bottom: 1px solid #eff1f5;
}

.subscription-debt-attempt-panels ::v-deep .v-expansion-panel-header {
  padding: 14px 0;
}

.subscription-debt-attempt-panels ::v-deep .v-expansion-panel-content__wrap {
  padding: 0 0 12px;
}

.subscription-debt-attempt-heading {
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-right: 8px;
}

.subscription-debt-attempt-heading > div {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  flex-direction: column;
  gap: 3px;
}

.subscription-debt-attempt-heading > .text-right {
  align-items: flex-end;
}

.subscription-debt-attempt-heading span {
  color: #7e8299;
  font-size: 11px;
  font-weight: 500;
}

.subscription-detail-list {
  display: grid;
  gap: 8px;
}

.subscription-detail-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  color: #7e8299;
  font-size: 12px;
}

.subscription-detail-row strong {
  color: #3f4254;
  text-align: right;
}

.subscription-pending-debts {
  overflow: hidden;
}

.subscription-pending-debt {
  display: grid;
  grid-template-columns: minmax(130px, 1.4fr) repeat(3, minmax(90px, 1fr));
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid #eff1f5;
  border-bottom: 1px solid #eff1f5;
}

.subscription-pending-debt + .subscription-pending-debt {
  border-top: 0;
}

.subscription-pending-debt > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.subscription-pending-debt span,
.subscription-pending-debt small {
  color: #7e8299;
  font-size: 10px;
  font-weight: 600;
}

.subscription-pending-debt strong {
  overflow: hidden;
  color: #3f4254;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.subscription-pending-debt-main strong {
  color: #181c32;
  font-size: 12px;
}

.subscription-pending-debt-plan span,
.subscription-pending-debt-plan strong {
  display: flex;
  align-items: center;
  gap: 3px;
}

.subscription-pending-debt-retry {
  align-self: flex-start;
  min-width: 0 !important;
  padding: 0 8px !important;
  font-size: 10px !important;
  font-weight: 700;
  letter-spacing: 0 !important;
  text-transform: none;
}

.subscription-status-chip {
  width: auto !important;
  flex: 0 0 auto !important;
}

.subscription-debt-attempt-heading .subscription-status-chip {
  align-self: flex-end;
}

.subscription-history-row {
  display: grid;
  grid-template-columns: minmax(150px, 1.2fr) auto minmax(130px, 1fr);
  align-items: center;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid #eff1f5;
  color: #3f4254;
  font-size: 11px;
}

.subscription-history-row:last-child {
  border-bottom: 0;
}

.subscription-history-row div {
  display: flex;
  flex-direction: column;
}
.subscription-history-row span {
  margin-top: 2px;
  color: #7e8299;
}

.subscription-history-amount {
  font-weight: 700;
  white-space: nowrap;
}

.subscription-detail-empty {
  padding: 12px;
  border-radius: 4px;
  background: #f7f8fa;
  color: #7e8299;
  font-size: 12px;
}

@media (max-width: 959px) {
  .subscription-segment-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .subscription-pending-debt {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .subscription-pending-debt-main {
    grid-column: 1 / -1;
  }
}
</style>
